// src/pages/GameScreen.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../firebase';
import logo from '../assets/logo.png'
import { getDatabase, ref, get, onValue } from 'firebase/database';
import { 
  ArrowLeft, Wallet, Trophy, Users, Calendar, Clock, 
  Lock, ChevronRight, Flame, Zap, Eye, Filter,
  Grid3x3, User, Users as UsersIcon, Crosshair, Gamepad2,
  TrendingUp, Award, Sparkles, Shield, Star, MapPin
} from 'lucide-react';

interface Tournament {
  id: string;
  title: string;
  date: string;
  time: string;
  image: string;
  prizePool: number;
  type: string;
  slots: number;
  registered: number;
  serverLogo: string;
  serverName: string;
  tier: string;
  organizerId: string;
  status: 'UPCOMING' | 'ONGOING' | 'COMPLETED';
  entryFee: number;
  mod: string;
  map?: string;
}

interface Category {
  id: string;
  name: string;
  modValue: string;
  icon: string;
  iconComponent: any;
  gradient: string;
}

// Categories with gaming-themed gradients
const BGMI_CATEGORIES: Category[] = [
  { id: 'bgmi_tdm_1v1', name: 'TDM 1v1', modValue: 'tdm_1v1', icon: 'crosshair', iconComponent: Crosshair, gradient: 'from-red-500 to-orange-500' },
  { id: 'bgmi_tdm_2v2', name: 'TDM 2v2', modValue: 'tdm_2v2', icon: 'users', iconComponent: Users, gradient: 'from-orange-500 to-yellow-500' },
  { id: 'bgmi_tdm_4v4', name: 'TDM 4v4', modValue: 'tdm_4v4', icon: 'users', iconComponent: Users, gradient: 'from-yellow-500 to-green-500' },
  { id: 'bgmi_classic_solo', name: 'Classic Solo', modValue: 'classic_solo', icon: 'user', iconComponent: User, gradient: 'from-green-500 to-teal-500' },
  { id: 'bgmi_classic_duo', name: 'Classic Duo', modValue: 'classic_duo', icon: 'users', iconComponent: Users, gradient: 'from-teal-500 to-blue-500' },
  { id: 'bgmi_classic_squad', name: 'Classic Squad', modValue: 'classic_squad', icon: 'users', iconComponent: Users, gradient: 'from-blue-500 to-purple-500' },
];

const FFMAX_CATEGORIES: Category[] = [
  { id: 'ffmax_br_solo', name: 'Solo BR', modValue: 'br_solo', icon: 'user', iconComponent: User, gradient: 'from-red-500 to-pink-500' },
  { id: 'ffmax_br_duo', name: 'Duo BR', modValue: 'br_duo', icon: 'users', iconComponent: Users, gradient: 'from-pink-500 to-purple-500' },
  { id: 'ffmax_br_squad', name: 'Squad BR', modValue: 'br_squad', icon: 'users', iconComponent: Users, gradient: 'from-purple-500 to-indigo-500' },
  { id: 'ffmax_cs_1v1', name: 'CS 1v1', modValue: 'cs_1v1', icon: 'crosshair', iconComponent: Crosshair, gradient: 'from-indigo-500 to-blue-500' },
  { id: 'ffmax_cs_2v2', name: 'CS 2v2', modValue: 'cs_2v2', icon: 'users', iconComponent: Users, gradient: 'from-blue-500 to-cyan-500' },
  { id: 'ffmax_cs_4v4', name: 'CS 4v4', modValue: 'cs_4v4', icon: 'users', iconComponent: Users, gradient: 'from-cyan-500 to-teal-500' },
  { id: 'ffmax_cs_6v6', name: 'CS 6v6', modValue: 'cs_6v6', icon: 'users', iconComponent: Users, gradient: 'from-teal-500 to-green-500' },
  { id: 'ffmax_lw_1v1', name: 'LW 1v1', modValue: 'lw_1v1', icon: 'crosshair', iconComponent: Crosshair, gradient: 'from-green-500 to-lime-500' },
  { id: 'ffmax_lw_2v2', name: 'LW 2v2', modValue: 'lw_2v2', icon: 'users', iconComponent: Users, gradient: 'from-lime-500 to-yellow-500' },
];

const GameScreen = () => {
  const navigate = useNavigate();
  const { game } = useParams<{ game: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [matches, setMatches] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'UPCOMING' | 'COMPLETED'>('UPCOMING');
  const [userTier, setUserTier] = useState<string>('T3');
  const [userOverrides, setUserOverrides] = useState<Record<string, string>>({});
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [hoveredMatch, setHoveredMatch] = useState<string | null>(null);

  useEffect(() => {
    if (game === 'BGMI') {
      setCategories(BGMI_CATEGORIES);
    } else if (game === 'FFMAX') {
      setCategories(FFMAX_CATEGORIES);
    }
  }, [game]);

  useEffect(() => {
    const fetchUserTier = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const db = getDatabase();
      const userRef = ref(db, `Accounts/${currentUser.uid}`);
      
      try {
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          setUserTier(data.defaultTier || 'T3');
          setUserOverrides(data.tierOverrides || {});
        }
      } catch (error) {
        console.error('Error fetching user tier:', error);
      }
    };

    fetchUserTier();
  }, []);

  useEffect(() => {
    if (!game) return;

    const fetchData = async () => {
      setLoading(true);
      const db = getDatabase();
      const tournamentsRef = ref(db, `Tournaments/${game}`);
      
      try {
        const snapshot = await get(tournamentsRef);
        const counts: Record<string, number> = {};
        
        categories.forEach(cat => {
          counts[cat.id] = 0;
        });
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const tournamentList: Tournament[] = [];
          
          Object.entries(data).forEach(([id, item]: [string, any]) => {
            
            tournamentList.push({
              id,
              title: item.title || '',
              date: item.date || '',
              time: item.time || '',
              image: item.image || '',
              prizePool: item.prizePool || 0,
              type: item.type || '',
              slots: item.slots || 0,
              registered: item.registered || 0,
              serverLogo: item.serverLogo || '',
              serverName: item.serverName || '',
              tier: item.tier || 'T3',
              organizerId: item.organizerId || '',
              status: item.status || 'UPCOMING',
              entryFee: item.entryFee || 0,
              mod: item.mod || '',
              map: item.map || 'Erangel',
            });
            
            if (item.status === 'UPCOMING' && item.mod) {
              const category = categories.find(cat => cat.modValue === item.mod);
              if (category) {
                counts[category.id] = (counts[category.id] || 0) + 1;
              }
            }
          });
          
          const parseDateTime = (dateStr: string, timeStr: string) => {
            if (!dateStr || !timeStr) return Date.now();
            const [day, month, year] = dateStr.split('/');
            const [time, modifier] = timeStr.split(' ');
            let [hours, minutes] = time.split(':').map(Number);
            if (modifier === 'PM' && hours < 12) hours += 12;
            if (modifier === 'AM' && hours === 12) hours = 0;
            return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
          };
          
          const statusPriority: Record<string, number> = { ONGOING: 0, UPCOMING: 1, COMPLETED: 2 };
          
          tournamentList.sort((a, b) => {
            const dateA = parseDateTime(a.date, a.time);
            const dateB = parseDateTime(b.date, b.time);
            if (statusPriority[a.status] !== statusPriority[b.status]) {
              return statusPriority[a.status] - statusPriority[b.status];
            }
            return a.status === 'COMPLETED' ? dateB - dateA : dateA - dateB;
          });
          
          setMatches(tournamentList);
          setCategoryCounts(counts);
        }
      } catch (error) {
        console.error('Error fetching tournaments:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (categories.length > 0) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [game, categories]);

  const parseDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr || !timeStr) return Date.now();
    const [day, month, year] = dateStr.split('/');
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes).getTime();
  };

  const getTimeRemaining = (dateStr: string, timeStr: string) => {
    const matchTime = parseDateTime(dateStr, timeStr);
    const now = Date.now();
    const diff = matchTime - now;
    
    if (diff <= 0) return 'Started';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const tierOrder = ['T3', 'T2Q', 'T2', 'T1', 'S'];
  const hasAccess = (tournament: Tournament) => {
    const override = userOverrides?.[tournament.organizerId];
    const effectiveTier = override || userTier;
    return tierOrder.indexOf(effectiveTier) >= tierOrder.indexOf(tournament.tier);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'ONGOING':
        return { text: 'LIVE', color: '#EF4444', bg: 'rgba(239,68,68,0.2)', icon: Flame };
      case 'COMPLETED':
        return { text: 'ENDED', color: '#6B7280', bg: 'rgba(107,114,128,0.2)', icon: Trophy };
      default:
        return { text: 'UPCOMING', color: '#10B981', bg: 'rgba(16,185,129,0.2)', icon: Zap };
    }
  };

  const formatINR = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filteredMatches = matches.filter(match => {
    if (selectedCategory && match.mod !== selectedCategory.modValue) return false;
    if (mode === 'UPCOMING') {
      return match.status === 'UPCOMING' || match.status === 'ONGOING';
    }
    return match.status === 'COMPLETED';
  });

if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
      
      {/* Animated Background Glow - Purple/Blue theme */}
      <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
      <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
      
      <div className="relative flex flex-col items-center">
        
        {/* Logo Container - Smaller size */}
        <div className="relative w-28 h-28">
          
          {/* Rotating Ring - Purple to Blue gradient */}
          <div className="absolute inset-0 rounded-full">
            <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
              <defs>
                <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#A855F7" />
                  <stop offset="50%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#EC4899" />
                </linearGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="44"
                fill="none"
                stroke="url(#ringGradient)"
                strokeWidth="3"
                strokeDasharray="200"
                strokeDashoffset="50"
                strokeLinecap="round"
              />
            </svg>
          </div>
          
          {/* Inner Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
          
          {/* Outer Ring Glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
          
          {/* Logo - Centered properly */}
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={logo}
              alt="ArenaX"
              className="w-20 h-20 object-contain animate-float"
            />
          </div>
        </div>

        {/* Loading Text with Gradient - Smaller text */}
        <div className="mt-5 text-center">
          <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
            LOADING ARENA...
          </p>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>

      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0a]/95 via-[#1a1a2e]/95 to-[#0a0a0a]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')}
              className="group relative w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center hover:from-purple-500/30 hover:to-blue-500/30 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
            </button>
            
            <div className="text-center">
              <h1 className="text-2xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent tracking-wider">
                {game}
              </h1>
              <div className="flex items-center justify-center gap-2 mt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-green-500 text-[10px] font-rajdhani font-bold tracking-wider">
                  {filteredMatches.length} ACTIVE MATCHES
                </span>
              </div>
            </div>
            
            <button 
              onClick={() => navigate('/dashboard/wallet')}
              className="group relative w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center hover:from-yellow-500/30 hover:to-orange-500/30 transition-all duration-300"
            >
              <Wallet className="w-5 h-5 text-yellow-500 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-24">
        {/* Category Filter Bar */}
        <div className="px-4 mb-6 overflow-x-auto hide-scrollbar">
          <div className="flex gap-3 min-w-max pb-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory(null)}
              className={`px-5 py-2.5 rounded-xl font-rajdhani font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                !selectedCategory 
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/25' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              All Games
            </motion.button>
            {categories.map((category) => {
              const Icon = category.iconComponent;
              const matchCount = categoryCounts[category.id] || 0;
              return (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 rounded-xl font-rajdhani font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                    selectedCategory?.id === category.id
                      ? `bg-gradient-to-r ${category.gradient} text-white shadow-lg`
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                  {matchCount > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      selectedCategory?.id === category.id
                        ? 'bg-white/20 text-white'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {matchCount}
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Mode Switcher */}
        {/* Mode Switcher */}
<div className="px-4 mb-6">
  <div className="bg-white/5 rounded-xl p-1 backdrop-blur-sm border border-white/10 relative">
    {/* Sliding Background Indicator */}
    <motion.div
      className="absolute top-1 bottom-1 w-1/2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg shadow-lg"
      animate={{
        x: mode === 'UPCOMING' ? '0%' : '100%',
      }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
    />
    
    <div className="flex gap-1 relative z-10">
      <button
        onClick={() => setMode('UPCOMING')}
        className={`flex-1 py-2.5 rounded-lg font-rajdhani font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
          mode === 'UPCOMING'
            ? 'text-white'
            : 'text-gray-400 hover:text-white'
        }`}
      >
        <Zap className="w-4 h-4" />
        LIVE & UPCOMING
      </button>
      <button
        onClick={() => setMode('COMPLETED')}
        className={`flex-1 py-2.5 rounded-lg font-rajdhani font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
          mode === 'COMPLETED'
            ? 'text-white'
            : 'text-gray-400 hover:text-white'
        }`}
      >
        <Trophy className="w-4 h-4" />
    COMPLETED
      </button>
    </div>
  </div>
</div>

        {/* Tournaments Grid */}
        <div className="px-4">
            <AnimatePresence mode="wait">
          {filteredMatches.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2 }}
              className="bg-white/5 rounded-2xl p-12 text-center border border-white/10 backdrop-blur-sm"
            >
              <Gamepad2 className="w-20 h-20 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 font-rajdhani text-lg">NO BATTLES FOUND</p>
              <p className="text-gray-500 text-sm mt-2 font-rajdhani">
                {selectedCategory ? `No ${mode.toLowerCase()} matches in ${selectedCategory.name}` : `No ${mode.toLowerCase()} matches available`}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredMatches.map((match, index) => {
                const status = getStatusConfig(match.status);
                const StatusIcon = status.icon;
                const locked = !hasAccess(match);
                const fillPercentage = (match.registered / match.slots) * 100;
                const isUrgent = match.status === 'UPCOMING';
                
                return (
                  <motion.div
                    key={match.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    onHoverStart={() => setHoveredMatch(match.id)}
                    onHoverEnd={() => setHoveredMatch(null)}
                    className="group relative bg-gradient-to-br from-white/5 to-white/0 rounded-2xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300 backdrop-blur-sm"
                  >
                    {/* Glow Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/0 to-blue-500/0 transition-all duration-500 ${
                      hoveredMatch === match.id ? 'from-purple-500/10 via-purple-500/5 to-blue-500/10' : ''
                    }`} />
                    
                    {/* Banner */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={match.image || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=400&fit=crop'} 
                        alt={match.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 left-3">
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani font-bold border backdrop-blur-sm flex items-center gap-1.5`} 
                             style={{ backgroundColor: status.bg, borderColor: status.color, color: status.color }}>
                          <StatusIcon className="w-3 h-3" />
                          {status.text}
                        </div>
                      </div>
                      
                      {/* Entry Fee Badge */}
                      <div className="absolute top-3 right-3">
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-rajdhani font-bold backdrop-blur-sm ${
                          match.entryFee > 0 
                            ? 'bg-gradient-to-r from-yellow-500/90 to-orange-500/90 text-white' 
                            : 'bg-gradient-to-r from-green-500/90 to-emerald-500/90 text-white'
                        }`}>
                          {match.entryFee > 0 ? formatINR(match.entryFee) : 'FREE ENTRY'}
                        </div>
                      </div>
                      
                      {/* Tier Badge */}
                      <div className="absolute bottom-3 left-3">
                        <div className="px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm border border-white/20">
                          <span className="text-white font-rajdhani text-xs font-bold">
                            <Shield className="w-3 h-3 inline mr-1" />
                            {match.tier} TOURNAMENT
                          </span>
                        </div>
                      </div>
                      
                      {/* Slots Fill Indicator */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${fillPercentage}%` }}
                          className={`h-full ${isUrgent ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-purple-500 to-blue-500'}`}
                        />
                      </div>
                      
                      {/* Lock Overlay */}
                      {locked && (
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center">
                          <Lock className="w-12 h-12 text-gray-400 mb-2" />
                          <span className="text-white font-rajdhani font-bold text-sm">{match.tier} ACCESS REQUIRED</span>
                          <span className="text-gray-400 text-xs mt-1">Upgrade to join</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-white font-rajdhani font-bold text-lg mb-3 line-clamp-1">
                        {match.title}
                      </h3>
                      
                      {/* Tournament Details */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="flex items-center gap-2 text-yellow-500">
                          <Trophy className="w-4 h-4" />
                          <div>
                            <p className="text-gray-500 text-[10px] font-rajdhani">PRIZE POOL</p>
                            <p className="text-white font-rajdhani font-bold text-sm">{formatINR(match.prizePool)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-purple-400">
                          <Users className="w-4 h-4" />
                          <div>
                            <p className="text-gray-500 text-[10px] font-rajdhani">FORMAT</p>
                            <p className="text-white font-rajdhani text-sm">{match.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-blue-400">
                          <Calendar className="w-4 h-4" />
                          <div>
                            <p className="text-gray-500 text-[10px] font-rajdhani">DATE</p>
                            <p className="text-white font-rajdhani text-sm">{match.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-cyan-400">
                          <Clock className="w-4 h-4" />
                          <div>
                            <p className="text-gray-500 text-[10px] font-rajdhani">TIME</p>
                            <p className="text-white font-rajdhani text-sm">{match.time}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Map & Server Info */}
                      <div className="flex items-center justify-between mb-4 p-2 bg-white/5 rounded-xl">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-300 text-sm font-rajdhani">{match.map || 'Erangel'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {match.serverLogo && (
                            <img src={match.serverLogo} alt={match.serverName} className="w-5 h-5 rounded-full" />
                          )}
                          <span className="text-gray-400 text-xs">{match.serverName}</span>
                        </div>
                      </div>
                      
                      {/* Players Progress */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                          <span className="font-rajdhani">REGISTERED PLAYERS</span>
                          <span className="font-rajdhani font-bold">{match.registered}/{match.slots}</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${fillPercentage}%` }}
                            className={`h-full rounded-full ${
                              fillPercentage === 100 ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-purple-500 to-blue-500'
                            }`}
                          />
                        </div>
                        {isUrgent && (
                          <p className="text-orange-400 text-xs mt-1 font-rajdhani flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            Only {match.slots - match.registered} spots left!
                          </p>
                        )}
                      </div>
                      
                      {/* Check if match is full */}
{(() => {
  const isFull = match.registered >= match.slots;
  
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{scale: 0.98 }}
      onClick={() =>navigate(`/tournament/${game}/${match.id}`)}
      className={`w-full py-3 rounded-xl text-white font-rajdhani font-bold text-sm transition-all duration-300 relative overflow-hidden group ${
        locked
        ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300'
          : match.status === 'ONGOING'
          ? 'bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg hover:shadow-red-500/25'
          : match.status === 'COMPLETED'
          ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300'
          : isFull
          ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:shadow-lg hover:shadow-red-500/25'
          : match.entryFee > 0
          ? 'bg-gradient-to-r from-purple-500 to-blue-500 hover:shadow-lg hover:shadow-purple-500/25'
          : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:shadow-lg hover:shadow-green-500/25'
      }`}
    >
      <span className="relative z-10">
        {locked ? '🔒 ACCESS REQUIRED' :
        match.status === 'ONGOING' ? 'JOIN LIVE BATTLE' : 
         match.status === 'COMPLETED' ? 'VIEW RESULTS' : 
         isFull ? '🔥 MATCH FULL' :
         match.entryFee > 0 ? `REGISTER - ${formatINR(match.entryFee)}` : '🎮 FREE REGISTRATION'}
      </span>
      {match.status !== 'COMPLETED' && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
      )}
    </motion.button>
  );
})()}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
          </AnimatePresence>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');

.font-rajdhani {
  font-family: 'Rajdhani', sans-serif;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

@keyframes spin-slow {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}

@keyframes pulse-slow {
  0%, 100% { 
    opacity: 0.3;
    transform: scale(1);
  }
  50% { 
    opacity: 0.6;
    transform: scale(1.1);
  }
}

@keyframes bounce {
  0%, 100% { 
    transform: translateY(0);
  }
  50% { 
    transform: translateY(-6px);
  }
}

.animate-spin-slow {
  animation: spin-slow 1.5s linear infinite;
}

.animate-float {
  animation: float 2.5s ease-in-out infinite;
}

.animate-pulse-slow {
  animation: pulse-slow 3s ease-in-out infinite;
}

.animate-bounce {
  animation: bounce 0.8s ease-in-out infinite;
}

.delay-1000 {
  animation-delay: 1s;
}`}</style>
    </div>
  );
};

export default GameScreen;